import { takeLatest, call, put, select, cancelled } from 'redux-saga/effects';
import i18next from 'i18next';

import * as meetingService from 'api/services/meeting';
import {
  LOAD_MA_MEETINGS,
  RESPOND_MA_MEETING_PROXY,
  LOAD_MA_MEETING_APPLICATION,
  SAVE_MA_MEETING_APPLICATION,
  LOAD_IPMA_MEETING_APPLICATIONS,
  EXPORT_IPMA_MEETING_APPLICATIONS,
  LOAD_IPMA_MEETING_APPLICATION,
  DECIDE_IPMA_MEETING_APPLICATION,
  SET_IPMA_MEETING_APPLICATION_VERIFIED,
  CHAIR_DECIDE_IPMA_MEETING_APPLICATION,
  LOAD_IPMA_MEETING_ATTENDANCE,
  LOAD_IPMA_MEETING_ATTENDANCE_APPLICATION,
  SAVE_IPMA_MEETING_ATTENDANCE,
} from 'store/constants';
import {
  setNewAlertCreator,
  setNewLoadingCreator,
  removeLoadingCreator,
  setMaMeetingsCreator,
  setMaMeetingsPagesCreator,
  setMaMeetingsTotalItemsCreator,
  loadMaMeetingsCreator,
  setMaMeetingApplicationCreator,
  setMaMeetingApplicationErrorsCreator,
  setIpmaMeetingApplicationsCreator,
  setIpmaMeetingApplicationsPagesCreator,
  setIpmaMeetingApplicationsTotalItemsCreator,
  setIpmaMeetingApplicationCreator,
  loadIpmaMeetingApplicationsCreator,
  setIpmaMeetingAttendanceCreator,
  setIpmaMeetingAttendanceErrorsCreator,
} from 'store/actionsCreators';
import { parseParams } from 'helpers';

const PROXIES = 'proxies';

/**
 * The green profile lists are keyed by meeting type, which is also what the API
 * takes - except for the attendance one: the store calls it "attendance"
 * because that is what the screen is, while the API knows it as the meta type
 * "ipma".
 */
const IPMA_LIST_TYPES = { attendance: 'ipma' };
const apiListType = (listType) => IPMA_LIST_TYPES[listType] || listType;

function* loadMaMeetings({ listType }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const [filters, page, perPage, sort] = yield select(({ ma }) => [
      ma.meetings[listType].filters,
      ma.meetings[listType].page,
      ma.meetings[listType].perPage,
      ma.meetings[listType].sort,
    ]);

    const params = parseParams({
      maxResult: perPage,
      page,
      'sort-by': sort,
      ...filters,
    });

    const response =
      listType === PROXIES
        ? yield call(meetingService.getMaMeetingProxies, params)
        : yield call(meetingService.getMaMeetings, listType, params);

    if (response.ok) {
      const {
        results: {
          result: { items, pageCount, totalItems },
        },
      } = yield response.json();

      yield put(setMaMeetingsCreator(listType, items));
      yield put(setMaMeetingsPagesCreator(listType, pageCount || 1));
      yield put(setMaMeetingsTotalItemsCreator(listType, totalItems));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(setMaMeetingsCreator(listType, []));
    yield put(setMaMeetingsPagesCreator(listType, 1));
    yield put(setMaMeetingsTotalItemsCreator(listType, 0));
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'meetings',
        status: 'error',
        text: yield call(errorText, error, i18next.t('page.ma.meetings.list.loadError')),
      }),
    );
    console.log(`[GET] ma meetings ${listType} error: `, error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* respondMaMeetingProxy({ payload: { id, approved } }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = yield call(meetingService.respondMaMeetingProxy, id, approved);

    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'meetings',
          status: 'success',
          text: approved
            ? i18next.t('page.ma.meetings.proxies.acceptSuccess')
            : i18next.t('page.ma.meetings.proxies.rejectSuccess'),
        }),
      );
      yield put(loadMaMeetingsCreator(PROXIES));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));

    let text = i18next.t('page.ma.meetings.proxies.respondError');

    try {
      const { errors } = yield error.response.json();
      if (errors && errors.length) {
        text = Array.isArray(errors) ? errors[0] : errors;
      }
    } catch (parseError) {
      console.log('[POST] ma meeting proxy respond error: ', parseError);
    }

    yield put(setNewAlertCreator({ type: 'meetings', status: 'error', text }));
    yield put(loadMaMeetingsCreator(PROXIES));
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* loadMaMeetingApplication({ payload: { meetingId, applicationId } }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = applicationId
      ? yield call(meetingService.getMaMeetingApplication, applicationId)
      : yield call(meetingService.getMaMeetingApplicationForm, meetingId);

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setMaMeetingApplicationCreator(result));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'meetings',
        status: 'error',
        text: yield call(errorText, error, i18next.t('page.ma.meetings.application.loadError')),
      }),
    );
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* saveMaMeetingApplication({
  payload: { applicationId, meetingType, submit, ...params },
  history,
}) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    yield put(setMaMeetingApplicationErrorsCreator({}));

    const body = { ...params, submit };
    const response = applicationId
      ? yield call(meetingService.updateMaMeetingApplication, applicationId, body)
      : yield call(meetingService.createMaMeetingApplication, body);

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setMaMeetingApplicationCreator(result));
      yield put(
        setNewAlertCreator({
          type: 'meetings',
          status: 'success',
          text: submit
            ? i18next.t('page.ma.meetings.application.submitSuccess')
            : i18next.t('page.ma.meetings.application.draftSuccess'),
        }),
      );

      if (submit) {
        history.push(`/ma/meetings/${meetingType}`);
      } else if (!applicationId && result.application) {
        // The draft exists now, so further saves have to update it.
        history.replace(`/ma/meetings/${meetingType}/application/${result.application.id}/edit`);
      }
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));

    let fields = {};
    let text = i18next.t('page.ma.meetings.application.saveError');

    try {
      const { errors, results } = yield error.response.json();
      fields = (results && results.result && results.result.fields) || {};
      if (errors && errors.length && !Object.keys(fields).length) {
        text = Array.isArray(errors) ? errors[0] : errors;
      }
    } catch (parseError) {
      console.log('[POST] ma meeting application error: ', parseError);
    }

    yield put(setMaMeetingApplicationErrorsCreator(fields));

    if (!Object.keys(fields).length) {
      yield put(setNewAlertCreator({ type: 'meetings', status: 'error', text }));
    }
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

/**
 * Both green profile lists read the same slice of the store, so the meeting
 * type doubles as the key of the list it belongs to.
 */
function* ipmaListParams(listType) {
  const list = yield select(({ ipma }) => ipma.meetings[listType]);

  if (!list) {
    return { filters: {}, page: 1, perPage: 10, sort: null };
  }

  const { filters, page, perPage, sort } = list;

  return { filters, page, perPage, sort };
}

function* loadIpmaMeetingApplications({ listType }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const { filters, page, perPage, sort } = yield call(ipmaListParams, listType);

    const response = yield call(
      meetingService.getIpmaMeetingApplications,
      apiListType(listType),
      parseParams({ maxResult: perPage, page, 'sort-by': sort, ...filters }),
    );

    if (response.ok) {
      const {
        results: {
          result: { items, pageCount, totalItems },
        },
      } = yield response.json();

      yield put(setIpmaMeetingApplicationsCreator(listType, items));
      yield put(setIpmaMeetingApplicationsPagesCreator(listType, pageCount || 1));
      yield put(setIpmaMeetingApplicationsTotalItemsCreator(listType, totalItems));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(setIpmaMeetingApplicationsCreator(listType, []));
    yield put(setIpmaMeetingApplicationsPagesCreator(listType, 1));
    yield put(setIpmaMeetingApplicationsTotalItemsCreator(listType, 0));
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'meetings',
        status: 'error',
        text: yield call(errorText, error, i18next.t('page.ma.meetings.list.loadError')),
      }),
    );
    console.log(`[GET] ipma meeting applications ${listType} error: `, error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

/**
 * The export runs against the filters of the list, so the file holds exactly
 * what the screen shows.
 */
function* exportIpmaMeetingApplications({ listType, filters: chosen }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    // The export screen brings its own filters; from the table it is whatever
    // the columns are filtered by.
    let params = chosen;

    if (!params) {
      const { filters, sort } = yield call(ipmaListParams, listType);

      params = { 'sort-by': sort, ...filters };
    }

    const response = yield call(
      meetingService.exportIpmaMeetingApplications,
      apiListType(listType),
      parseParams(params),
    );

    if (response.ok) {
      const blob = yield response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${listType}-meeting-applications.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'meetings',
        status: 'error',
        text: yield call(errorText, error, i18next.t('page.ipma.meetings.list.exportError')),
      }),
    );
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* loadIpmaMeetingApplication({ payload: { applicationId } }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = yield call(meetingService.getIpmaMeetingApplication, applicationId);

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setIpmaMeetingApplicationCreator(result));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'meetings',
        status: 'error',
        text: yield call(errorText, error, i18next.t('page.ma.meetings.application.loadError')),
      }),
    );
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* decideIpmaMeetingApplication({
  payload: { applicationId, meetingType, accept, reason },
  history,
}) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = yield call(meetingService.decideIpmaMeetingApplication, applicationId, {
      accept,
      reason,
    });

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setIpmaMeetingApplicationCreator(result));
      yield put(
        setNewAlertCreator({
          type: 'meetings',
          status: 'success',
          text: accept
            ? i18next.t('page.ipma.meetings.application.acceptSuccess')
            : i18next.t('page.ipma.meetings.application.rejectSuccess'),
        }),
      );

      if (history) {
        history.push(`/ipma/meetings/${meetingType}`);
      }
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'meetings',
        status: 'error',
        text: yield call(errorText, error, i18next.t('page.ipma.meetings.application.decideError')),
      }),
    );
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

/**
 * IPMA records the one flag it keeps on an application - the paid invoice of a
 * COD seat, or simply that the application was verified. The list is reloaded
 * afterwards, so the switch shows what the API actually stored.
 */
function* setIpmaMeetingApplicationVerified({ payload: { applicationId, meetingType, verified } }) {
  try {
    const response = yield call(
      meetingService.setIpmaMeetingApplicationVerified,
      applicationId,
      verified,
    );

    if (response.ok) {
      yield put(loadIpmaMeetingApplicationsCreator(meetingType));
    }
  } catch (error) {
    yield put(
      setNewAlertCreator({
        type: 'meetings',
        status: 'error',
        text: yield call(errorText, error, i18next.t('page.ipma.meetings.list.invoiceError')),
      }),
    );
    yield put(loadIpmaMeetingApplicationsCreator(meetingType));
  }
}

/**
 * The Chair of the CoD passes an observer application on to the people that
 * decide, or turns it down there and then.
 */
function* chairDecideIpmaMeetingApplication({
  payload: { applicationId, meetingType, accept, reason },
  history,
}) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = yield call(meetingService.chairDecideIpmaMeetingApplication, applicationId, {
      approve: accept,
      reason,
    });

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setIpmaMeetingApplicationCreator(result));
      yield put(
        setNewAlertCreator({
          type: 'meetings',
          status: 'success',
          text: accept
            ? i18next.t('page.ipma.meetings.application.chairApproveSuccess')
            : i18next.t('page.ipma.meetings.application.chairRejectSuccess'),
        }),
      );

      if (history) {
        history.push(`/ipma/meetings/${meetingType}`);
      }
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'meetings',
        status: 'error',
        text: yield call(errorText, error, i18next.t('page.ipma.meetings.application.chairError')),
      }),
    );
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

/**
 * The attendance form of the reader themselves: which of the open meetings
 * they are coming to. There is one per person, so it is loaded without an id.
 */
function* loadIpmaMeetingAttendance() {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = yield call(meetingService.getIpmaMeetingAttendance);

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setIpmaMeetingAttendanceCreator(result));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'meetings',
        status: 'error',
        text: yield call(errorText, error, i18next.t('page.ipma.meetings.attendance.loadError')),
      }),
    );
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

/**
 * One attendance form somebody else sent, as the people deciding read it.
 */
function* loadIpmaMeetingAttendanceApplication({ payload: { applicationId } }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = yield call(meetingService.getIpmaMeetingAttendanceApplication, applicationId);

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setIpmaMeetingAttendanceCreator(result));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'meetings',
        status: 'error',
        text: yield call(errorText, error, i18next.t('page.ipma.meetings.attendance.loadError')),
      }),
    );
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* saveIpmaMeetingAttendance({ payload: { submit, answers }, history }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    yield put(setIpmaMeetingAttendanceErrorsCreator({}));

    const response = yield call(meetingService.saveIpmaMeetingAttendance, { submit, answers });

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setIpmaMeetingAttendanceCreator(result));
      yield put(
        setNewAlertCreator({
          type: 'meetings',
          status: 'success',
          text: submit
            ? i18next.t('page.ipma.meetings.attendance.submitSuccess')
            : i18next.t('page.ipma.meetings.attendance.draftSuccess'),
        }),
      );

      if (submit && history) {
        history.push('/ipma/meetings/attendance');
      }
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));

    let fields = {};
    let text = i18next.t('page.ipma.meetings.attendance.saveError');

    try {
      const { errors, results } = yield error.response.json();
      fields = (results && results.result && results.result.fields) || {};
      if (errors && errors.length && !Object.keys(fields).length) {
        text = Array.isArray(errors) ? errors[0] : errors;
      }
    } catch (parseError) {
      console.log('[POST] ipma meeting attendance error: ', parseError);
    }

    yield put(setIpmaMeetingAttendanceErrorsCreator(fields));

    if (!Object.keys(fields).length) {
      yield put(setNewAlertCreator({ type: 'meetings', status: 'error', text }));
    }
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

async function errorText(error, fallback) {
  try {
    const { errors } = await error.response.json();
    return errors && errors.length ? errors[0] : fallback;
  } catch (parseError) {
    return fallback;
  }
}

export default function* watchMeeting() {
  yield takeLatest(LOAD_MA_MEETINGS, loadMaMeetings);
  yield takeLatest(RESPOND_MA_MEETING_PROXY, respondMaMeetingProxy);
  yield takeLatest(LOAD_MA_MEETING_APPLICATION, loadMaMeetingApplication);
  yield takeLatest(SAVE_MA_MEETING_APPLICATION, saveMaMeetingApplication);
  yield takeLatest(LOAD_IPMA_MEETING_APPLICATIONS, loadIpmaMeetingApplications);
  yield takeLatest(EXPORT_IPMA_MEETING_APPLICATIONS, exportIpmaMeetingApplications);
  yield takeLatest(LOAD_IPMA_MEETING_APPLICATION, loadIpmaMeetingApplication);
  yield takeLatest(DECIDE_IPMA_MEETING_APPLICATION, decideIpmaMeetingApplication);
  yield takeLatest(SET_IPMA_MEETING_APPLICATION_VERIFIED, setIpmaMeetingApplicationVerified);
  yield takeLatest(CHAIR_DECIDE_IPMA_MEETING_APPLICATION, chairDecideIpmaMeetingApplication);
  yield takeLatest(LOAD_IPMA_MEETING_ATTENDANCE, loadIpmaMeetingAttendance);
  yield takeLatest(LOAD_IPMA_MEETING_ATTENDANCE_APPLICATION, loadIpmaMeetingAttendanceApplication);
  yield takeLatest(SAVE_IPMA_MEETING_ATTENDANCE, saveIpmaMeetingAttendance);
}
